// Settings defaults + merge, extracted verbatim from the safety-histogram pilot
// (dev @ a3ff9f7) under #2.

/**
 * Settings the histogram no longer has (#188; @jwildfire, 2026-10-03). Each
 * put a p-value on the chart that was worked out in JavaScript with a
 * shortcut; the histogram now draws no p-value, and leaves the test to R.
 * v1.9.1 deprecated them and v1.10.0 removed them. A caller that still passes
 * one gets a chart without the annotation and one line in the console.
 */
export const REMOVED_SETTINGS = ['test_normality', 'compare_distributions'];

/**
 * What the console says, once per chart, when a removed setting is passed.
 * @param {string} name The setting.
 * @returns {string} The sentence.
 */
export const removedSetting = (name) =>
  `safety.viz histogram: \`${name}\` was removed in v1.10.0 and is ignored. ` +
  'The histogram draws no p-value: run the test in R.';

/**
 * Rendering and data-mapping settings for the histogram module. Every key
 * has a default in DEFAULT_SETTINGS; callers pass only the overrides they
 * need and syncSettings fills in the rest. Field-list settings (filters,
 * groups, details) accept column-name strings or { value_col, label }
 * objects, in a single value or an array.
 * @typedef {Object} HistogramSettings
 * @property {string} [measure_col='TEST'] Column holding the measure name; required in the data. Each distinct measure (with its unit) becomes an option in the Measure control.
 * @property {string} [value_col='STRESN'] Column holding the numeric result; required in the data. Rows with missing or non-numeric values are removed with a console warning.
 * @property {string} [id_col='USUBJID'] Participant identifier column; drives the participant counts shown above the chart and the default listing columns.
 * @property {string} [unit_col='STRESU'] Unit column, appended to the measure name in labels when present.
 * @property {string} [normal_col_low='STNRLO'] Lower-limit-of-normal column; with normal_col_high, feeds the normal-range overlay. The control hides for measures without normal-range data (SH-FUNC-004C).
 * @property {string} [normal_col_high='STNRHI'] Upper-limit-of-normal column; see normal_col_low.
 * @property {Array<string|Object>} [filters=[]] Filter controls: column names or { value_col, label } specs. Filters whose column is absent from the data are dropped with a console warning. Filter specs take `{ value_col, label, start, all, multiple }`: `start` is the opening selection (an array for a `multiple` filter, and a start of `0` or `false` is a real value, not an absent one); `all` controls the "All" option and defaults to true whether or not a start is given — pass `all: false` to remove it, and the filter then always holds one value, the first unless `start` names another; a start value the data lacks is dropped with a console warning; `multiple: true` renders a checkbox multiselect whose state is null (everything) or an array of values (#136).
 * @property {Array<string|Object>} [groups=[]] Group-by options for the small-multiple charts; a "None" option is always offered first.
 * @property {?Array<string|Object>} [details=null] Columns for the linked participant listing; when null, defaults to participant ID, the filter columns, result, normal limits, and unit.
 * @property {?string} [start_value=null] Measure selected on first render. When null (the default) the histogram opens on the all-measures overview — one small-multiple histogram per measure, click one to drill in. A measure absent from the data falls back to the overview with a console warning.
 * @property {?string[]} [measures=null] Ordered whitelist of measures the Measure control offers: only these appear, and in this order. Entries match the label the control shows (the measure name with its unit appended where `unit_col` is mapped), falling back to the bare `measure_col` value, which picks up every unit variant of that measure. null or [] offers every measure in the data, alphabetically — the behaviour before this setting existed. Configured measures absent from the data are dropped with a console warning; when none of them is present the control falls back to every measure in the data rather than going blank (SH-MEAS-001, SH-MEAS-002).
 * @property {string} [bin_algorithm="Scott's normal reference rule"] Binning algorithm applied on first render; one of the ALGORITHMS options. Setting an explicit bin quantity or width in the UI switches it to "Custom".
 * @property {boolean} [normal_range=true] Offer the Show Normal Range control (visible only for measures with normal-range data).
 * @property {boolean} [display_normal_range=false] Draw the normal-range overlay on first render. The pilot's camelCase alias displayNormalRange is still honored.
 * @property {boolean} [annotate_bin_boundaries=false] Label the x-axis with bin boundaries instead of linear ticks on first render.
 * @property {string} [group_by='sh_none'] Column the small multiples are grouped by on first render; 'sh_none' disables grouping. Unknown columns are added to the group options as-is.
 * @property {?string} [studyday_col=null] Numeric study-day column for the docked profile's labs-over-time x-axis; when null the profile falls back to input order (#99, PPRF-SH-001).
 * @property {?string} [visit_col=null] Visit-name column passed to the docked profile for point tooltips (#99, PPRF-SH-001).
 * @property {?string} [visitn_col=null] Numeric visit column passed to the docked profile for point ordering context (#99, PPRF-SH-001).
 * @property {?Object} [measure_values=null] Optional map of short profile keys (ALT/AST/TB/ALP) to this data's full measure names, passed to the docked profile so the key liver measures resolve; null keeps the profile module's defaults (#99, PPRF-SH-001).
 * @property {boolean} [profile=true] Dock the shared participant-profile module in the shell's profile slot, fed by the linked listing's row-click focus via the participantsSelected event; false restores the pre-#99 listing-only detail (#99, PPRF-SH-001).
 * @property {?Array<string|Object>} [profile_details=null] Demographic columns for the docked profile's header, as names or { value_col, label } specs; null shows none (the host `details` are listing columns, not demographics) (#99, PPRF-SH-001).
 * @property {?string} [participantProfileURL=null] Optional link-out URL for the docked profile's header, templated by every literal `{id}` token (#99, PPRF-SH-001).
 * @property {string} [width='100%'] Widget width, carried over from the pilot API for the R widget bindings; the current shell always spans its container.
 * @property {number} [height=460] Chart-area height in pixels, carried over from the pilot API for the R widget bindings; the current shell fixes the chart area at 460px.
 * @property {number} [page_size=10] Rows per page in the linked participant listing.
 */

/**
 * Built-in defaults for every histogram setting; syncSettings merges caller
 * overrides onto these.
 * @type {HistogramSettings}
 */
import { normalizeFilterSpec } from '../filters.js';

export const DEFAULT_SETTINGS = {
  measure_col: 'TEST',
  value_col: 'STRESN',
  id_col: 'USUBJID',
  unit_col: 'STRESU',
  normal_col_low: 'STNRLO',
  normal_col_high: 'STNRHI',
  filters: [],
  groups: [],
  details: null,
  start_value: null,
  measures: null,
  bin_algorithm: "Scott's normal reference rule",
  normal_range: true,
  display_normal_range: false,
  annotate_bin_boundaries: false,
  group_by: 'sh_none',
  studyday_col: null,
  visit_col: null,
  visitn_col: null,
  measure_values: null,
  profile: true,
  profile_details: null,
  participantProfileURL: null,
  width: '100%',
  height: 460,
  page_size: 10
};

/**
 * Binning algorithms offered by the Bins control; valid values for the
 * bin_algorithm setting.
 */
export const ALGORITHMS = [
  'Square-root choice',
  "Sturges' formula",
  'Rice Rule',
  "Scott's normal reference rule",
  "Freedman-Diaconis' choice",
  "Shimazaki and Shinomoto's choice",
  'Custom'
];

/**
 * Normalize a single value or nullish to an array.
 * @private
 */
export function arrayify(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

/**
 * Normalize a column name or spec object to { value_col, label }.
 * @private
 */
export function fieldSpec(value, fallbackLabel) {
  if (typeof value === 'string') return { value_col: value, label: fallbackLabel || value };
  return { value_col: value.value_col, label: value.label || value.value_col };
}

/**
 * Merge caller settings onto DEFAULT_SETTINGS and normalize them: field
 * lists become { value_col, label } arrays, the group list always offers
 * None, group_by falls back when its column is not offered, listing details
 * default from the other mappings, and the pilot's displayNormalRange alias
 * is honored.
 * @param {HistogramSettings} settings Caller overrides; pass {} for the defaults.
 * @returns {HistogramSettings} The merged, normalized settings.
 */
export function syncSettings(settings) {
  const synced = { ...DEFAULT_SETTINGS, ...settings };
  // A removed setting is not carried: nothing downstream reads one (#188).
  for (const name of REMOVED_SETTINGS) delete synced[name];

  synced.measures = arrayify(synced.measures);
  synced.filters = arrayify(synced.filters)
    .map((value) => normalizeFilterSpec(value))
    .filter((d) => d.value_col);
  const defaultGroup = { value_col: 'sh_none', label: 'None' };
  synced.groups = [
    defaultGroup,
    ...arrayify(synced.groups)
      .map(fieldSpec)
      .filter((d) => d.value_col)
  ];
  if (synced.group_by && !synced.groups.some((group) => group.value_col === synced.group_by)) {
    synced.groups.push({ value_col: synced.group_by, label: synced.group_by });
  }
  synced.group_by = synced.groups.some((group) => group.value_col === synced.group_by)
    ? synced.group_by
    : synced.groups[0].value_col;
  synced.details = arrayify(synced.details)
    .map(fieldSpec)
    .filter((d) => d.value_col);
  if (!synced.details.length) {
    synced.details = [
      { value_col: synced.id_col, label: 'Participant ID' },
      // Listing columns, not filters: take only the column and its label, so the
      // filter contract's start/all/multiple keys do not leak into the listing.
      ...synced.filters.map((filter) => fieldSpec(filter)),
      { value_col: synced.value_col, label: 'Result' },
      { value_col: synced.normal_col_low, label: 'Lower Limit of Normal' },
      { value_col: synced.normal_col_high, label: 'Upper Limit of Normal' },
      { value_col: synced.unit_col, label: 'Unit' }
    ].filter((d) => d.value_col);
  }
  if (settings.displayNormalRange !== undefined)
    synced.display_normal_range = settings.displayNormalRange;

  // Docked-profile pass-throughs (#99): profile is a plain boolean and
  // profile_details normalizes to a spec array only when provided (null keeps
  // the "no header demographics" default — the host `details` are listing
  // columns, not demographics).
  synced.profile = Boolean(synced.profile);
  synced.profile_details =
    synced.profile_details === undefined || synced.profile_details === null
      ? null
      : arrayify(synced.profile_details)
          .map((value) => fieldSpec(value))
          .filter((d) => d.value_col);

  return synced;
}
